<template>
    <kiosk-layout formContainer>
        <step-form-heading :icon="faBuilding">
            Register Brand
            <template v-slot:description>
                Registers the legal entity in The Campaign Registry. Vetting is requested from the brand page once TCR
                reports it verified.
            </template>
        </step-form-heading>

        <form-wrapper
            :sections="[
                { id: 'details', label: 'Details' },
                { id: 'address', label: 'Address' },
                { id: 'advanced', label: 'Advanced' },
            ]"
        >
            <kiosk-tcr-brand-form-details
                id="details"
                v-model="form"
                :enums="enums"
                :externalResults="$externalResults"
            />
            <kiosk-tcr-brand-form-address id="address" v-model="form" :externalResults="$externalResults" />
            <kiosk-tcr-brand-form-advanced
                id="advanced"
                v-model="form"
                :enums="enums"
                :externalResults="$externalResults"
            />

            <vx-sticky-footer>
                <vx-button href="/kiosk/ten-dlc/brands" class="w-20" size="lg" color="muted"> Cancel </vx-button>
                <vx-button
                    @click="submit()"
                    data-test="submit-button"
                    class="grow"
                    size="lg"
                    color="primary"
                    :loading="submitting"
                >
                    Register
                </vx-button>
            </vx-sticky-footer>
        </form-wrapper>
    </kiosk-layout>
</template>

<script lang="ts" setup>
import { faBuilding } from '@fortawesome/pro-solid-svg-icons';
import { VxButton, VxStickyFooter } from '@voxie/frontend-components';
import useVuelidate from '@vuelidate/core';
import { ref } from 'vue';
import FormWrapper from '~/components/general/FormWrapper.vue';
import StepFormHeading from '~/components/general/StepFormHeading.vue';
import KioskLayout from '~/components/kiosk/KioskLayout.vue';
import KioskTcrBrandFormAddress from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandFormAddress.vue';
import KioskTcrBrandFormAdvanced from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandFormAdvanced.vue';
import KioskTcrBrandFormDetails from '~/components/kiosk/kiosk-ten-dlc/registration/KioskTcrBrandFormDetails.vue';
import { brandDefaults } from '~/components/kiosk/kiosk-ten-dlc/registration/registrationFormDefaults';
import { useCallout } from '~/composables/useCallout';
import { useFormRequestHandler } from '~/composables/useFormRequestHandler';
import { useToasted } from '~/composables/useToasted';
import { adminTenDlcRegistrationClient } from '~/services';
import type { TcrEnums } from '~/services/adminTenDlcRegistrationClient.types';
import dayjs from '~/utils/dayjs';
import { escapeHtml } from '~/utils/html';
import { validateRules } from '~/utils/validation';

const { $externalResults, catchError } = useFormRequestHandler();
const toasted = useToasted();
const callout = useCallout();

const form = ref(brandDefaults());
const enums = ref<TcrEnums | null>(null);
// Owns the external results so validateRules() clears them for every section before a resubmit.
const validate = useVuelidate({ $externalResults });
const submitting = ref(false);

adminTenDlcRegistrationClient
    .getEnums()
    .then((response) => {
        enums.value = response.data;
    })
    .catch((e) => {
        console.error(e);
        toasted.global.platform_error();
    });

const submit = async () => {
    if (submitting.value) {
        return;
    }

    submitting.value = true;
    validate.value.$reset();

    if (!(await validateRules(validate))) {
        submitting.value = false;
        return;
    }

    try {
        const response = await adminTenDlcRegistrationClient.createBrand(form.value);

        callout.success(
            'kiosk-tcr-brand',
            `<strong>${escapeHtml(
                response.data.display_name ?? response.data.brand_id
            )}</strong> registered as ${escapeHtml(response.data.brand_id)}`,
            {
                ttl: dayjs().add(30, 'seconds'),
                shadow: true,
            }
        );

        window.location.assign(`/kiosk/ten-dlc/brands/${response.data.brand_id}`);
    } catch (e) {
        catchError(e);
    } finally {
        submitting.value = false;
    }
};
</script>
